import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { StatusBadge } from '@pazportal/ui';
import { useFieldTrips } from '../hooks/useFieldTrips';
import { supabase } from '../lib/supabase';
import { formatDate, formatSolutionType } from '../lib/formatters';
import { TripBillingPanel, TripEditForm, TripStatusActions } from '../components/trips/TripControls';
import { useTripBilling } from '../hooks/useTripBilling';
import { useTripLinks } from '../hooks/useTripContinuity';
import { OpenItemsPanel } from '../components/trips/OpenItemsPanel';
import { isOverdue, localToday, type TripStatus } from '../lib/tripLifecycle';

type Status = 'all' | 'review' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

type Trip = {
  id: string;
  project_number: string;
  scope: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  po_number?: string | null;
  solution_types?: string[] | null;
  deployment_id?: string | null;
  lead_technician_id?: string | null;
  run_id?: string | null;
  run_stop?: number | null;
  imported_at?: string | null;
  customer?: { id: string; name: string } | null;
  location?: { id: string; name: string } | null;
  lead_technician?: { id: string; full_name: string | null; email: string | null } | null;
};

function useDailyReportCount(tripId: string | null) {
  return useQuery({
    queryKey: ['admin-trip-daily-report-count', tripId],
    enabled: Boolean(tripId),
    queryFn: async () => {
      const { count, error } = await supabase
        .from('daily_reports')
        .select('id', { count: 'exact', head: true })
        .eq('trip_id', tripId!);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60 * 1000,
  });
}

export function TripsPage() {
  const [filter, setFilter] = useState<Status>('all');
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightTrip = searchParams.get('trip');
  const highlightDeployment = searchParams.get('deployment');
  const queryFilter =
    filter === 'all' || filter === 'review'
      ? undefined
      : { status: filter as TripStatus };
  const { data, isLoading, isError } = useFieldTrips(queryFilter);
  const today = localToday();
  const allTrips = useMemo(() => (data ?? []) as unknown as Trip[], [data]);
  const trips = useMemo(
    () => (filter === 'review' ? allTrips.filter((t) => isOverdue(t, today)) : allTrips),
    [allTrips, filter, today],
  );
  // Only meaningful while the list holds open trips (All / Needs review).
  const overdueCount = filter === 'all' || filter === 'review' ? allTrips.filter((t) => isOverdue(t, today)).length : null;
  const [openId, setOpenId] = useState<string | null>(null);

  // Deep links (?trip= / ?deployment=) open the drawer once the list is in.
  useEffect(() => {
    if (openId || trips.length === 0) return;
    const match = trips.find(
      (t) => (highlightTrip && t.id === highlightTrip) || (highlightDeployment && t.deployment_id === highlightDeployment),
    );
    if (match) setOpenId(match.id);
  }, [trips, highlightTrip, highlightDeployment, openId]);

  const openTrip = trips.find((t) => t.id === openId) ?? null;

  const close = () => {
    setOpenId(null);
    if (highlightTrip || highlightDeployment) {
      const next = new URLSearchParams(searchParams);
      next.delete('trip');
      next.delete('deployment');
      setSearchParams(next, { replace: true });
    }
  };

  const filterBtns: { key: Status; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'review', label: 'Needs review' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Field Trips</h1>
          <p className="text-sm text-text-muted mt-0.5">Select a trip to see its details and daily reports.</p>
        </div>
        <div className="flex gap-1 overflow-x-auto max-w-full" role="group" aria-label="Filter by status">
          {filterBtns.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={`px-3 py-1.5 min-h-[44px] sm:min-h-0 text-xs font-medium rounded-md whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                filter === f.key
                  ? 'bg-primary text-text-inverse'
                  : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {overdueCount != null && overdueCount > 0 && filter !== 'review' && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning bg-warning-soft px-4 py-3 text-sm text-warning">
          <span>
            {overdueCount} trip{overdueCount === 1 ? ' is' : 's are'} still open after the end date. Confirm each one completed, or cancel or reschedule it.
          </span>
          <button type="button" onClick={() => setFilter('review')} className="rounded px-3 py-1.5 min-h-[40px] text-xs font-medium bg-surface text-text hover:bg-surface-strong">
            Review
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted" role="status">Loading trips…</div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-notification">Could not load field trips. Refresh to try again.</div>
      ) : trips.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
          {filter === 'review' ? 'Nothing to review — every open trip is still within its dates.' : filter === 'all' ? 'No field trips yet. Trips are scheduled from a deployment on Customer 360, planned as a run on Trip Runs, or imported from Zoho deals on Trip Imports.' : 'No trips match this filter.'}
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {trips.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setOpenId(t.id)}
                aria-haspopup="dialog"
                className={`w-full text-left rounded-lg border bg-surface p-3 min-h-[44px] active:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  t.id === openId ? 'border-primary ring-1 ring-primary' : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text">{t.project_number}</p>
                    <p className="text-xs text-text-muted truncate">
                      {t.customer?.name ?? 'Unknown customer'} · {t.location?.name ?? 'No location'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={t.status ?? 'scheduled'} />
                    {isOverdue(t, today) && <span className="text-[11px] font-medium text-warning">Overdue</span>}
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-1.5 line-clamp-2">{t.scope ?? 'No scope recorded'}</p>
                <p className="text-xs text-text-faint mt-1">
                  {t.start_date ? formatDate(t.start_date) : 'No start date'}
                  {t.end_date ? ` → ${formatDate(t.end_date)}` : ''}
                  {t.lead_technician?.full_name ? ` · ${t.lead_technician.full_name}` : ''}
                </p>
              </button>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-3">
                <tr className="text-left">
                  <th className="px-3 py-2.5 font-medium">Project</th>
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium">Location</th>
                  <th className="px-3 py-2.5 font-medium">Lead</th>
                  <th className="px-3 py-2.5 font-medium">Dates</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {trips.map((t) => (
                  <tr
                    key={t.id}
                    className={`bg-surface hover:bg-surface-strong cursor-pointer ${t.id === openId ? 'bg-primary-soft' : ''}`}
                    onClick={() => setOpenId(t.id)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenId(t.id);
                      }
                    }}
                    aria-haspopup="dialog"
                  >
                    <td className="px-3 py-2.5 font-mono text-text">{t.project_number}</td>
                    <td className="px-3 py-2.5 text-text">{t.customer?.name ?? '—'}</td>
                    <td className="px-3 py-2.5 text-text-muted">{t.location?.name ?? '—'}</td>
                    <td className="px-3 py-2.5 text-text-muted">{t.lead_technician?.full_name ?? t.lead_technician?.email ?? 'Unassigned'}</td>
                    <td className="px-3 py-2.5 text-xs text-text-muted whitespace-nowrap">
                      {t.start_date ? formatDate(t.start_date) : '—'}
                      {t.end_date ? ` → ${formatDate(t.end_date)}` : ''}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={t.status ?? 'scheduled'} />
                        {isOverdue(t, today) && <span className="text-xs font-medium text-warning">Overdue</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {openTrip && <TripDrawer trip={openTrip} onClose={close} />}
    </div>
  );
}

function TripDrawer({ trip, onClose }: { trip: Trip; onClose: () => void }) {
  const { data: reportCount, isLoading: countLoading, isError: countError } = useDailyReportCount(trip.id);
  const { data: billing } = useTripBilling(trip.id);
  const { data: links } = useTripLinks(trip.id);
  const [editing, setEditing] = useState(false);
  const status = (trip.status ?? 'scheduled') as TripStatus;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const solutions = (trip.solution_types ?? []).map(formatSolutionType);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end bg-black/40" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="trip-drawer-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:w-[420px] max-h-[85vh] sm:max-h-none sm:h-full overflow-y-auto bg-surface border-t sm:border-t-0 sm:border-l border-border rounded-t-lg sm:rounded-none shadow-xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-text-faint">Field trip</p>
            <h2 id="trip-drawer-title" className="text-lg font-semibold text-text font-mono break-all">{trip.project_number}</h2>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={trip.status ?? 'scheduled'} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close trip details"
              className="min-w-[44px] min-h-[44px] sm:min-w-[32px] sm:min-h-[32px] rounded-md text-text-muted hover:text-text hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              ×
            </button>
          </div>
        </div>

        <div className="mt-4">
          <TripStatusActions tripId={trip.id} status={status} billingStatus={billing?.billing_status ?? null} />
        </div>

        {editing ? (
          <div className="mt-4">
            <TripEditForm
              trip={{
                id: trip.id,
                start_date: trip.start_date,
                end_date: trip.end_date,
                lead_technician_id: trip.lead_technician_id ?? null,
                scope: trip.scope,
                po_number: trip.po_number ?? null,
              }}
              onDone={() => setEditing(false)}
            />
          </div>
        ) : (
        <>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs font-medium text-primary hover:underline min-h-[32px]"
          >
            Reschedule / reassign
          </button>
        </div>
        <dl className="mt-1 space-y-2.5 text-sm">
          <Item label="Customer">
            {trip.customer ? <Link to={`/customers/${trip.customer.id}`} className="text-primary hover:underline">{trip.customer.name}</Link> : 'Unknown customer'}
          </Item>
          <Item label="Location">
            {trip.location ? <Link to={`/locations/${trip.location.id}`} className="text-primary hover:underline">{trip.location.name}</Link> : 'No location'}
          </Item>
          <Item label="Dates">
            {trip.start_date ? formatDate(trip.start_date) : 'No start date'}
            {trip.end_date ? ` → ${formatDate(trip.end_date)}` : ''}
          </Item>
          <Item label="Lead technician">{trip.lead_technician?.full_name ?? trip.lead_technician?.email ?? 'Unassigned'}</Item>
          <Item label="Daily reports">
            {countLoading ? 'Counting…' : countError ? 'Could not load' : `${reportCount ?? 0} submitted`}
          </Item>
          {trip.run_id && (
            <Item label="Run">
              <Link to={`/runs?run=${trip.run_id}`} className="text-primary hover:underline">
                Stop {trip.run_stop ?? '?'} of a run
              </Link>
            </Item>
          )}
          {trip.imported_at && <Item label="Source">Imported from Zoho CRM history</Item>}
          {(links ?? []).map((l) => (
            <Item key={l.id} label={l.system === 'zoho_crm_deal' ? 'Zoho deal' : l.system === 'zoho_books_invoice' ? 'Books invoice' : 'Zoho link'}>
              {l.url ? (
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-words">{l.label ?? l.external_id}</a>
              ) : (
                <span className="break-words">{l.label ?? l.external_id}</span>
              )}
            </Item>
          ))}
          {trip.po_number && <Item label="PO number">{trip.po_number}</Item>}
          {solutions.length > 0 && <Item label="Solutions">{solutions.join(', ')}</Item>}
          <Item label="Scope">
            <span className="whitespace-pre-wrap break-words">{trip.scope ?? 'No scope recorded'}</span>
          </Item>
        </dl>
        </>
        )}

        {trip.location && (
          <div className="mt-5 border-t border-divider pt-4">
            <OpenItemsPanel locationId={trip.location.id} tripId={trip.id} />
          </div>
        )}

        <div className="mt-5 border-t border-divider pt-4">
          <TripBillingPanel tripId={trip.id} />
        </div>

        <p className="mt-5 text-xs text-text-muted">
          Daily reports are written by the field crew in the on-site app; the customer-facing sections are published to the customer portal.
        </p>
      </aside>
    </div>
  );
}

function Item({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3">
      <dt className="text-text-muted text-xs pt-0.5">{label}</dt>
      <dd className="text-text min-w-0">{children}</dd>
    </div>
  );
}
